/* Position SVG parts on an outer group; animate an inner group so CSS never
   replaces their coordinates. Each profile plant has a bounded silhouette. */
import type { CSSProperties, ReactNode } from 'react';
import type { AvatarId } from '../state/ProfileContext';
import { plantGrowth } from '../state/plantGrowth';

interface VineProps {
  streak: number;
  avatar?: AvatarId;
  size?: number;
  reducedMotion?: boolean;
}

export function Vine({ streak, avatar = 'sprout', size = 160, reducedMotion = false }: VineProps) {
  const growth = plantGrowth(avatar, streak);
  const p = growth.progress;
  const height = 28 + p * 112;
  const top = 174 - height;
  const count = 2 + Math.floor(p * 8);
  const stemStyle: CSSProperties = reducedMotion ? {} : {
    strokeDasharray: 1, strokeDashoffset: 1,
    animation: 'cv-grow-vine var(--dur-slower) var(--ease-out) forwards',
  };
  const part = (key: string | number, transform: string, children: ReactNode, delay = 0) => (
    <g key={key} transform={transform} data-plant-anchor="">
      <g data-plant-growth="" style={reducedMotion ? undefined : {
        transformOrigin: '0px 0px',
        animation: 'cv-leaf-sprout var(--dur-slow) var(--ease-out) both',
        animationDelay: `${240 + delay * 55}ms`,
      }}>{children}</g>
    </g>
  );
  const stem = (d: string, width = 3) => (
    <path d={d} stroke="var(--vine)" strokeWidth={width} strokeLinecap="round" pathLength={1} style={stemStyle} />
  );
  const leaf = (length = 30, width = 10) => <>
    <path d={`M0 0 C${length * .3} ${-width} ${length * .8} ${-width} ${length} 0 C${length * .7} ${width} ${length * .3} ${width} 0 0Z`} fill="var(--vine-bright)" />
    <path d={`M1 0H${length - 3}`} stroke="var(--vine-deep)" strokeWidth="1" />
  </>;
  const pairedLeaves = (amount: number, spread = 32, upper = top) => Array.from({ length: amount }, (_, i) => {
    const y = 160 - i * (160 - upper) / Math.max(1, amount - 1);
    return part(i, `translate(100 ${y}) scale(${i % 2 === 0 ? -1 : 1} 1) rotate(-28)`, leaf(spread, 8 + p * 3), i);
  });

  let plant: ReactNode;
  if (growth.days === 0) {
    plant = <path d="M100 167C107 171 107 179 100 182C93 179 93 171 100 167Z" fill="var(--seed)" />;
  } else if (avatar === 'leaf') {
    const length = 38 + p * 98;
    plant = <>
      {stem('M100 174Q88 161 88 154')}
      {part('blade', 'translate(88 154) rotate(-67)', <>
        {leaf(length, 13 + p * 29)}
        {Array.from({ length: 2 + Math.floor(p * 6) }, (_, i) => {
          const x = 10 + i * (length - 24) / (2 + Math.floor(p * 6));
          return <path key={i} d={`M${x} 0l12 ${-7 - p * 10}M${x} 0l12 ${7 + p * 10}`} stroke="var(--vine-deep)" strokeWidth="1" />;
        })}
      </>)}
    </>;
  } else if (avatar === 'fern') {
    plant = <>
      {stem(`M100 174V${top + 20}Q100 ${top - 4} 116 ${top + 2}Q127 ${top + 8} 120 ${top + 18}Q115 ${top + 23} 112 ${top + 17}`)}
      {Array.from({ length: count }, (_, i) => {
        const y = 160 - i * (height - 22) / count;
        const length = (24 + p * 25) * (1 - i / (count + 2) * .65);
        return [-1, 1].map(side => part(`${i}-${side}`, `translate(100 ${y}) scale(${side} 1) rotate(-32)`, leaf(length, 4 + p * 2), i));
      })}
    </>;
  } else if (avatar === 'clover') {
    plant = Array.from({ length: 1 + Math.floor(p * 6) }, (_, i) => {
      const x = 100 + (i === 0 ? 0 : (i % 2 ? -1 : 1) * (19 + Math.floor((i - 1) / 2) * 17));
      const y = 112 - p * 50 + (i % 3) * 18;
      const r = 8 + p * 4;
      return <g key={i}>
        {stem(`M100 174Q${x} 151 ${x} ${y}`, 2)}
        {part(i, `translate(${x} ${y})`, <>
          {[0, 120, 240].map(angle => <path key={angle} transform={`rotate(${angle})`} d={`M0 0C${-r * 2} ${-r} ${-r} ${-r * 3} 0 ${-r * 1.5}C${r} ${-r * 3} ${r * 2} ${-r} 0 0Z`} fill="var(--vine-bright)" stroke="var(--vine-deep)" strokeWidth=".8" />)}
          <circle r="2" fill="var(--seed)" />
        </>, i)}
      </g>;
    });
  } else if (avatar === 'flower' || avatar === 'bud') {
    const blossomY = 126 - p * 66;
    plant = <>
      {stem(`M100 174V${blossomY}`)}
      {pairedLeaves(2 + Math.floor(p * 2), 22 + p * 8, blossomY + 23)}
      {part('bloom', `translate(100 ${blossomY})`, growth.days >= 3 ? <>
        {Array.from({ length: avatar === 'flower' ? 8 : 6 }, (_, i) => <ellipse key={i} cx="0" cy={-12 - p * 8} rx={avatar === 'flower' ? 7 + p * 5 : 10 + p * 5} ry={13 + p * 9} transform={`rotate(${i * (avatar === 'flower' ? 45 : 60)})`} fill={avatar === 'flower' ? 'var(--seed)' : 'var(--berry)'} stroke="var(--paper-raised)" strokeWidth="1" />)}
        <circle r={7 + p * 4} fill={avatar === 'flower' ? 'var(--berry)' : 'var(--seed)'} />
      </> : <>
        <path d="M0 6C-18 -5 -13 -23 0 -26C13 -23 18 -5 0 6Z" fill={avatar === 'flower' ? 'var(--seed)' : 'var(--berry)'} />
        <path d="M-11 -3L0 9L11 -3" stroke="var(--vine)" strokeWidth="4" strokeLinecap="round" />
      </>, count)}
    </>;
  } else if (avatar === 'seed') {
    const shootTop = 155 - p * 112;
    plant = <>
      <path d={`M100 174L100 190M100 178l${-6 - p * 15} 10M100 181l${7 + p * 13} 9`} stroke="var(--seed)" strokeWidth="2" strokeLinecap="round" />
      <path d="M100 163C112 169 112 179 100 184C88 179 88 169 100 163Z" fill="var(--seed)" />
      {stem(`M100 173V${shootTop}`)}
      {Array.from({ length: 2 + Math.floor(p * 6) }, (_, i) => part(i, `translate(100 ${shootTop + 9 + i * (150 - shootTop) / (2 + Math.floor(p * 6))}) scale(${i % 2 ? 1 : -1} 1) rotate(-30)`, leaf(20 + p * 10, 8), i))}
    </>;
  } else if (avatar === 'palm') {
    const crown = 148 - p * 60;
    plant = <>
      <path d={`M94 174Q103 124 100 ${crown}L104 ${crown}Q113 124 107 174Z`} fill="var(--seed)" />
      {Array.from({ length: 3 + Math.floor(p * 5) }, (_, i) => <path key={i} d={`M99 ${165 - i * (155 - crown) / (2 + Math.floor(p * 5))}l8 -3`} stroke="var(--vine-deep)" strokeWidth="1.5" />)}
      {Array.from({ length: 3 + Math.floor(p * 4) }, (_, i) => {
        const angle = -170 + i * 160 / (2 + Math.floor(p * 4));
        return part(i, `translate(102 ${crown}) rotate(${angle})`, <>
          <path d={`M0 0Q${30 + p * 14} -28 ${44 + p * 23} 5Q${29 + p * 13} -6 0 0Z`} fill="var(--vine-bright)" />
          <path d={`M0 0Q${30 + p * 14} -16 ${44 + p * 23} 5`} stroke="var(--vine-deep)" strokeWidth="1" />
        </>, i);
      })}
    </>;
  } else {
    plant = <>
      {stem(`M100 174Q96 167 100 160V${top}`)}
      {pairedLeaves(count)}
      {growth.days >= 14 ? part('bud', `translate(100 ${top})`, <circle r="5" fill="var(--berry)" />, count) : null}
    </>;
  }

  return (
    <svg className="cv-growing-plant" data-plant={avatar} data-growth-days={growth.days}
      key={`${avatar}-${growth.days}`} width={size} height={size} viewBox="0 0 200 200"
      fill="none" role="img" aria-label={`${growth.name}, ${growth.days}-day streak: ${growth.stage}`}>
      <ellipse cx="100" cy="177" rx="69" ry="6" fill="var(--paper-sunken)" />
      {plant}
    </svg>
  );
}
